import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Alert, Button, DataTable, Pagination } from "../../../ui";
import type { DataColumn } from "../../../ui";
import { HookFormField } from "../../../app/forms/HookFormField";
import type { Schemas } from "../../workspace/api.generated";
import { date, money } from "../../workspace/model";
import { Kpi, PageHeader, Panel, RowAction, RowLink } from "../../workspace/shared";
import { transactionSearchFormSchema } from "../schema";
import type { TransactionSearchFormValues } from "../schema";

export interface TransactionListViewProps {
  readonly filters: TransactionSearchFormValues;
  readonly result?: Schemas["AdminBoPageTransactionReviewQueueView"];
  readonly state?: "ready" | "loading" | "error";
  readonly refreshing?: boolean;
  readonly notice?: string;
  readonly detailHref?: (id: number) => string;
  readonly onSearch: (filters: TransactionSearchFormValues) => void;
  readonly onPageChange: (page: number) => void;
  readonly onRetry: () => void;
  readonly onViewTransaction: (id: number) => void;
  readonly now?: string;
  readonly onExport?: () => void;
  readonly exporting?: boolean;
  readonly exportError?: string;
}
export function TransactionListView({ filters, result, state = "ready", refreshing = false, notice, detailHref, onSearch, onPageChange, onRetry, onViewTransaction, onExport, exporting, exportError }: TransactionListViewProps) {
  const form = useForm<TransactionSearchFormValues>({ resolver: zodResolver(transactionSearchFormSchema), values: filters, shouldFocusError: true });
  const summary = state === "ready" ? result : undefined;
  const pageAmount = summary?.pageAmount ?? summary?.items.reduce((sum, row) => sum + row.requestedAmount, 0);
  const columns: readonly DataColumn<Schemas["TransactionReviewQueueView"]>[] = [
    { key: "date", title: "등록시각", render: row => date(row.resubmittedAt ?? row.submittedAt) },
    { key: "member", title: "회원", render: row => row.memberName ?? "정보 없음" },
    { key: "phone", title: "휴대폰번호", render: row => row.phoneNumber },
    { key: "category", title: "종류", render: row => row.categoryName },
    { key: "title", title: "거래명", render: row => row.title },
    { key: "amount", title: "금액", numeric: true, render: row => money(row.requestedAmount) },
    { key: "submission", title: "상태", render: row => row.submissionType === "NEW" ? "신규" : row.submissionType === "RESUBMITTED" ? "재접수" : "확인 필요" },
    { key: "review", title: "심사", render: row => detailHref
      ? <RowLink href={detailHref(row.transactionId)} onNavigate={() => onViewTransaction(row.transactionId)} triggerKey={`transaction:${row.transactionId}:detail`}>심사 · {row.displayTransactionNo ?? row.transactionNo}</RowLink>
      : <RowAction onClick={() => onViewTransaction(row.transactionId)}>심사 · {row.displayTransactionNo ?? row.transactionNo}</RowAction> },
  ];
  return <>
    <PageHeader id="ADM-S07" title="거래 심사" description="" hideBody meta={refreshing ? <p role="status">최신 목록 확인 중</p> : undefined} />
    {notice ? <Alert persistent variant="information" title="거래 목록 복귀">{notice}</Alert> : null}
    {exportError ? <Alert persistent variant="danger" title="다운로드 실패">{exportError}</Alert> : null}
    <Panel title="검색 조건"><form className="admin-filter-bar" noValidate onSubmit={form.handleSubmit(values => onSearch({ ...values, keyword: values.keyword.trim() }))}>
      <HookFormField compact formControl={form.control} label="접수 시작일" name="from" type="date" />
      <HookFormField compact formControl={form.control} label="접수 종료일" name="to" type="date" />
      <HookFormField compact control="select" formControl={form.control} label="상태" name="status"><option value="">전체</option><option value="NEW">신규</option><option value="RESUBMITTED">재접수</option></HookFormField>
      <HookFormField compact formControl={form.control} label="이름 또는 휴대폰" name="keyword" maxLength={100} />
      <Button size="small" type="submit">조회</Button>
    </form></Panel>
    <div className="admin-kpi-grid">
      <Kpi label="검색 건수" value={summary ? `${summary.totalCount.toLocaleString("ko-KR")}건` : "—"} />
      <Kpi label="현재 페이지 요청 금액" value={pageAmount === undefined ? "—" : money(pageAmount)} />
    </div>
    <div className="admin-list-actions"><Button size="small" variant="secondary" disabled={!onExport || exporting} isLoading={exporting} onClick={onExport}>다운로드</Button></div>
    <DataTable caption="거래 심사 검색 결과" columns={columns} rows={state === "ready" ? result?.items ?? [] : []} rowKey={row => row.transactionId} state={state} onRetry={onRetry} />
    {result && state === "ready" ? <Pagination page={result.page} size={result.size} totalCount={result.totalCount} onPageChange={onPageChange} /> : null}
  </>;
}
